import { IndicationYesNoOptions } from "../helpers";

import { EditMetadata, QualityRegimeOptions } from "./MetaData";
import { Task } from "./Task";

export const samplingStandardOptions = [
  {
    value: "NEN5744v1991",
    label: "NEN 5744 v1991",
  },
  {
    value: "NEN5744v2011-A1v2013",
    label: "NEN 5744 v2011+A1 v2013",
  },
  {
    value: "NEN5745v1997",
    label: "NEN 5745 v1997",
  },
  {
    value: "NTA8017v2008",
    label: "NTA 8017 v2008",
  },
  {
    value: "NTA8017v2016",
    label: "NTA 8017 v2016",
  },
  {
    value: "SIKBProtocol2002vanafV4",
    label: "SIKB Protocol 2002 vanaf V4",
  },
  {
    value: "onbekend",
    label: "Onbekend",
    onlyImbroA: true,
  },
] as const;
export const samplingStandardOptionsType = samplingStandardOptions.map(({ value }) => value);
export type SamplingStandardOptions = (typeof samplingStandardOptionsType)[number];

export const colorOptions = [
  { value: "wit", label: "Wit" },
  { value: "grijs", label: "Grijs" },
  { value: "zwart", label: "Zwart" },
  { value: "rood", label: "Rood" },
  { value: "oranje", label: "Oranje" },
  { value: "geel", label: "Geel" },
  { value: "groen", label: "Groen" },
  { value: "blauw", label: "Blauw" },
  { value: "paars", label: "Paars" },
  { value: "bruin", label: "Bruin" },
  { value: "roestbruin", label: "Roestbruin" },
  { value: "beige", label: "Beige" },
  { value: "creme", label: "Creme" },
  { value: "kleurloos", label: "Kleurloos" },
] as const;
export const colorOptionsType = colorOptions.map(({ value }) => value);
export type ColorOptions = (typeof colorOptionsType)[number];

export const colorStrengthOptions = [
  { value: "zeer licht", label: "Zeer licht" },
  { value: "licht", label: "Licht" },
  { value: "neutraal", label: "Neutraal" },
  { value: "donker", label: "Donker" },
  { value: "zeer donker", label: "Zeer donker" },
] as const;
export const colorStrengthOptionsType = colorStrengthOptions.map(({ value }) => value);
export type ColorStrengthOptions = (typeof colorStrengthOptionsType)[number];

export const limitSymbolOptions = [
  { value: "GT", label: "GT" },
  { value: "LT", label: "LT" },
] as const;
export const limitSymbolOptionsType = limitSymbolOptions.map(({ value }) => value);
export type LimitSymbolOptions = (typeof limitSymbolOptionsType)[number];

export const pumpTypeOptions = [
  { value: "onderwaterpomp", label: "Onderwaterpomp" },
  { value: "peristaltischePomp", label: "Peristaltische pomp" },
  { value: "vacuümpomp", label: "Vacuümpomp" },
  { value: "anders", label: "Anders" },
  { value: "onbekend", label: "Onbekend", onlyImbroA: true },
] as const;
export const pumpTypeOptionsType = pumpTypeOptions.map(({ value }) => value);
export type PumpTypeOptions = (typeof pumpTypeOptionsType)[number];

export const statusQualityControlOptions = [
  { value: "goedgekeurd", label: "Goedgekeurd" },
  { value: "afgekeurd", label: "Afgekeurd" },
  { value: "onbeslist", label: "Onbeslist" },
  { value: "onbekend", label: "Onbekend", onlyImbroA: true },
] as const;
export const statusQualityControlOptionsType = statusQualityControlOptions.map(
  ({ value }) => value
);
export type QualityControlStatusOptions = (typeof statusQualityControlOptionsType)[number];

export const qualityControlMethodOptions = [
  {
    value: "handboekProvinciesRIVMv2017",
    label: "Handboek provincies RIVM v2017",
  },
  {
    value: "oordeelDeskundige",
    label: "Oordeel deskundige",
  },
  {
    value: "qCProtocolProvinciesEnRIVMv2021",
    label: "QC Protocol provincies en RIVM v2021",
  },
  {
    value: "onbekend",
    label: "Onbekend",
    onlyImbroA: true,
  },
] as const;
export const qualityControlMethodOptionsType = qualityControlMethodOptions.map(
  ({ value }) => value
);
export type QualityControlMethodOptions = (typeof qualityControlMethodOptionsType)[number];

export type GARTask = {
  projectNumber: string;
  fieldworkFile: Blob | undefined;
  labFile: Blob | undefined;

  metadata: {
    qualityRegime: QualityRegimeOptions;
    samplingOperator: string; // kvk nummer
    samplingStandard: SamplingStandardOptions; // Bemonsteringsprocedure
    qualityControlMethod: QualityControlMethodOptions; // Beoordelingsprocedure
    responsibleLaboratoryKvk: string; // kvk nummer
    groundwaterMonitoringNets: string[]; // gmn id's
    deliveryAccountableParty: string; // kvk nummer
  };
};

export type FieldResearch = {
  samplingDateTime: string;
  samplingOperator?: string;
  samplingStandard: SamplingStandardOptions;
  pumpType: PumpTypeOptions;
  primaryColour?: ColorOptions;
  secondaryColour?: ColorOptions;
  colourStrength?: ColorStrengthOptions;
  abnormalityInCooling: IndicationYesNoOptions;
  abnormalityInDevice: IndicationYesNoOptions;
  pollutedByEngine: IndicationYesNoOptions;
  filterAerated: IndicationYesNoOptions;
  groundWaterLevelDroppedTooMuch: IndicationYesNoOptions;
  abnormalFilter: IndicationYesNoOptions;
  sampleAerated: IndicationYesNoOptions;
  hoseReused: IndicationYesNoOptions;
  temperatureDifficultToMeasure: IndicationYesNoOptions;
  fieldMeasurements?: FieldMeasurement[];
};

export type FieldMeasurement = {
  parameter: number;
  unit: string;
  fieldMeasurementValue: number;
  qualityControlStatus: QualityControlStatusOptions;
};

export type Analysis = {
  parameter: number;
  unit: string;
  analysisMeasurementValue?: number;
  limitSymbol?: string;
  reportingLimit?: number;
  qualityControlStatus: string;
};

export type AnalysisProcess = {
  date: string;
  analyticalTechnique?: string;
  validationMethod?: string;
  analyses: Analysis[];
};

export type LaboratoryAnalysis = {
  responsibleLaboratoryKvk?: string;
  analysisProcesses: AnalysisProcess[];
};

type EditGARData = {
  objectIdAccountableParty: string;
  qualityControlMethod: QualityControlMethodOptions; // Beoordelingsprocedure
  gmwBroId: string;
  tubeNumber: string;
  fieldResearch: FieldResearch;
  laboratoryAnalyses: LaboratoryAnalysis[];
};

export type EditGARTask = Task<EditGARData, EditMetadata>;
